import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { reportService } from '@/services/reportService';
import * as ReportTypes from '@/types/report.types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, Download, TrendingUp, Users, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';

interface PrematureStatisticsReportProps {
    hospitalId?: number;
    startDate?: string;
    endDate?: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export const PrematureStatisticsReport: React.FC<PrematureStatisticsReportProps> = ({
    hospitalId,
    startDate,
    endDate
}) => {
    const [data, setData] = useState<ReportTypes.PrematureStatistics | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const result = await reportService.getPrematureStatistics({
                    hospital_id: hospitalId,
                    start_date: startDate,
                    end_date: endDate
                });
                setData(result);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Eroare la încărcarea datelor');
                console.error('Error fetching premature statistics:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [hospitalId, startDate, endDate]);

    const handleExportPdf = async () => {
        if (!data) return;
        
        try {
            const blob = await reportService.exportToPdf({
                report_data: data,
                report_type: 'premature_statistics',
                title: 'Statistici pacienți prematuri'
            });
            
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `statistici_prematuri_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            toast({
                title: "Export reușit",
                description: "Raportul a fost exportat ca PDF"
            });
        } catch (err) {
            toast({
                title: "Eroare la export",
                description: "Nu s-a putut exporta raportul ca PDF",
                variant: "destructive"
            });
        }
    };

    const handleExportExcel = async () => {
        if (!data) return;
        
        try {
            const blob = await reportService.exportToExcel({
                report_data: data,
                report_type: 'premature_statistics',
                title: 'Statistici pacienți prematuri'
            });
            
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `statistici_prematuri_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            toast({
                title: "Export reușit",
                description: "Raportul a fost exportat ca Excel"
            });
        } catch (err) {
            toast({
                title: "Eroare la export",
                description: "Nu s-a putut exporta raportul ca Excel",
                variant: "destructive"
            });
        }
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="h-32 bg-gray-200 rounded"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="destructive">
                <Info className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    if (!data) {
        return (
            <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>Nu există date de afișat</AlertDescription>
            </Alert>
        );
    }

    // Transform data for charts
    const gestationalAgeData = data.by_gestational_age?.map(item => ({
        name: item.gestational_age_group,
        value: item.count,
        percentage: item.percentage
    })) || [];

    const birthWeightData = data.by_birth_weight?.map(item => ({
        name: item.birth_weight_group,
        value: item.count,
        percentage: item.percentage
    })) || [];

    const vonSummaryData = data.von_summary?.map(item => ({
        name: item.section,
        value: item.completed,
        total: item.total,
        percentage: item.percentage
    })) || [];

    return (
        <div className="space-y-6">
            {/* Header with export buttons */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold">Statistici pacienți prematuri</h2>
                    <p className="text-muted-foreground">
                        Total pacienți prematuri: <span className="font-semibold">{data.total_premature_patients}</span>
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={handleExportPdf}>
                        <Download className="h-4 w-4 mr-2" />
                        Export PDF
                    </Button>
                    <Button variant="outline" onClick={handleExportExcel}>
                        <Download className="h-4 w-4 mr-2" />
                        Export Excel
                    </Button>
                </div>
            </div>

            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Pacienți Prematuri</CardTitle>
                        <Users className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{data.total_premature_patients}</div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Rata supraviețuire</CardTitle>
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{data.key_outcomes?.survival_rate || 0}%</div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Vârstă Gestațională Medie</CardTitle>
                        <Activity className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{data.average_metrics?.avg_gestational_age ? Number(data.average_metrics.avg_gestational_age).toFixed(1) : 0} săptămâni</div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Greutate medie la naștere</CardTitle>
                        <Activity className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{data.average_metrics?.avg_birth_weight ? Number(data.average_metrics.avg_birth_weight).toFixed(0) : 0}g</div>
                    </CardContent>
                </Card>
            </div>

            {/* Charts and Tables */}
            <Tabs defaultValue="gestational-age" className="space-y-4">
                <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="gestational-age">Vârstă gestatională</TabsTrigger>
                    <TabsTrigger value="birth-weight">Greutate la naștere</TabsTrigger>
                    <TabsTrigger value="outcomes">Rezultate cheie</TabsTrigger>
                    <TabsTrigger value="von-summary">Sumar RONN</TabsTrigger>
                </TabsList>

                <TabsContent value="gestational-age" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Distribuție după Vârsta Gestatională</CardTitle>
                            <CardDescription>
                                Numărul și procentajul pacienților prematuri pe grupe de vârstă gestatională
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="h-80">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={gestationalAgeData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip 
                                            formatter={(value, name) => [
                                                name === 'value' ? `${value} pacienți` : `${value}%`,
                                                name === 'value' ? 'Număr' : 'Procentaj'
                                            ]}
                                        />
                                        <Bar dataKey="value" fill="#8884d8" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="mt-4">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Grupă vârstă gestatională</TableHead>
                                            <TableHead>Număr pacienți</TableHead>
                                            <TableHead>Procentaj</TableHead>
                                            <TableHead>Greutate medie</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {data.by_gestational_age?.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell className="font-medium">{item.gestational_age_group}</TableCell>
                                                <TableCell>{item.count}</TableCell>
                                                <TableCell>{item.percentage}%</TableCell>
                                                <TableCell>{item.avg_birth_weight ? Number(item.avg_birth_weight).toFixed(0) : '-'}g</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="birth-weight" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Distribuție după greutatea la naștere</CardTitle>
                            <CardDescription>
                                Numărul și procentajul pacienților pe grupe de greutate
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="h-80">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={birthWeightData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={({ name, percentage }) => `${name}: ${percentage}%`}
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {birthWeightData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value) => [`${value} pacienți`, 'Număr']} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="mt-4">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Grupă greutate</TableHead>
                                            <TableHead>Număr pacienți</TableHead>
                                            <TableHead>Procentaj</TableHead>
                                            <TableHead>Vârstă gest. medie</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {data.by_birth_weight?.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell className="font-medium">{item.birth_weight_group}</TableCell>
                                                <TableCell>{item.count}</TableCell>
                                                <TableCell>{item.percentage}%</TableCell>
                                                <TableCell>{item.avg_gestational_age ? Number(item.avg_gestational_age).toFixed(1) : '-'} săptămâni</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="outcomes" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Rezultate cheie</CardTitle>
                            <CardDescription>
                                Indicatori clinici importanți pentru pacienții prematuri
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-sm font-medium">Rata supraviețuire</span>
                                        <Badge variant="secondary">{data.key_outcomes?.survival_rate || 0}%</Badge>
                                    </div>
                                    <Progress value={data.key_outcomes?.survival_rate || 0} className="h-2" />
                                </div>

                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-sm font-medium">BPD</span>
                                        <Badge variant="secondary">{data.key_outcomes?.bpd_rate || 0}%</Badge>
                                    </div>
                                    <Progress value={data.key_outcomes?.bpd_rate || 0} className="h-2" />
                                </div>

                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-sm font-medium">ROP</span>
                                        <Badge variant="secondary">{data.key_outcomes?.rop_rate || 0}%</Badge>
                                    </div>
                                    <Progress value={data.key_outcomes?.rop_rate || 0} className="h-2" />
                                </div>

                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-sm font-medium">NEC</span>
                                        <Badge variant="secondary">{data.key_outcomes?.nec_rate || 0}%</Badge>
                                    </div>
                                    <Progress value={data.key_outcomes?.nec_rate || 0} className="h-2" />
                                </div>

                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-sm font-medium">IVH</span>
                                        <Badge variant="secondary">{data.key_outcomes?.ivh_rate || 0}%</Badge>
                                    </div>
                                    <Progress value={data.key_outcomes?.ivh_rate || 0} className="h-2" />
                                </div>

                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-sm font-medium">Durata Medie Spitalizare</span>
                                        <Badge variant="secondary">{data.average_metrics?.avg_length_of_stay ? Number(data.average_metrics.avg_length_of_stay).toFixed(0) : 0} zile</Badge>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="von-summary" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Sumar date RONN</CardTitle>
                            <CardDescription>
                                Completitudinea secțiunilor din baza de date RONN
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {vonSummaryData.map((item, index) => (
                                    <div key={index} className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-sm font-medium">{item.name}</span>
                                            <Badge variant={item.percentage >= 80 ? "default" : item.percentage >= 50 ? "secondary" : "destructive"}>
                                                {item.value}/{item.total} ({item.percentage}%)
                                            </Badge>
                                        </div>
                                        <Progress value={item.percentage} className="h-2" />
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};
